<template>
  <div class="summary-container">
    <div class="summary-cards">
      <div class="card">총 결제금액: {{ formatNumber(totalPaid) }} 원</div>
      <div class="card">총 충전 LYD: {{ formatNumber(totalCharged) }} LYD</div>
      <div class="card">
        총 보상 지급: {{ formatNumber(totalRewarded) }} LYD
      </div>
      <div class="card">총 사용 LYD: {{ formatNumber(totalDeducted) }} LYD</div>
    </div>

    <h3 style="margin-top: 2rem">월별 결제 통계</h3>
    <table class="daily-table">
      <thead>
        <tr>
          <th>월</th>
          <th>결제금액 (원)</th>
          <th>충전된 LYD</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(entry, month) in sortedMonthlyCharges"
          :key="month"
          @click="selectMonth(month, 'charge')"
        >
          <td>{{ month }}</td>
          <td>{{ formatNumber(entry.totalPrice) }}</td>
          <td>{{ formatNumber(entry.totalLydia) }}</td>
        </tr>
      </tbody>
    </table>

    <table
      class="daily-table"
      v-if="selectedMonth && selectedMode === 'charge'"
    >
      <thead>
        <tr>
          <th>일자</th>
          <th>결제금액 (원)</th>
          <th>충전된 LYD</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(entry, date) in sortedDailyCharges" :key="date">
          <td>{{ date }}</td>
          <td>{{ formatNumber(entry.totalPrice) }}</td>
          <td>{{ formatNumber(entry.totalLydia) }}</td>
        </tr>
      </tbody>
    </table>

    <h3 style="margin-top: 2rem">월별 보상 통계</h3>
    <table class="daily-table">
      <thead>
        <tr>
          <th>월</th>
          <th>dailyCheckin</th>
          <th>writePost</th>
          <th>writeComment</th>
          <th>refJoin</th>
          <th>합계</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(entry, month) in sortedMonthlyRewards"
          :key="month"
          @click="selectMonth(month, 'reward')"
        >
          <td>{{ month }}</td>
          <td>{{ formatNumber(entry.dailyCheckin ?? 0) }}</td>
          <td>{{ formatNumber(entry.writePost ?? 0) }}</td>
          <td>{{ formatNumber(entry.writeComment ?? 0) }}</td>
          <td>{{ formatNumber(entry.refJoin ?? 0) }}</td>
          <td>{{ formatNumber(entry.total ?? 0) }}</td>
        </tr>
      </tbody>
    </table>

    <table
      class="daily-table"
      v-if="selectedMonth && selectedMode === 'reward'"
    >
      <thead>
        <tr>
          <th>일자</th>
          <th>dailyCheckin</th>
          <th>writePost</th>
          <th>writeComment</th>
          <th>refJoin</th>
          <th>합계</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(entry, date) in sortedDailyRewards" :key="date">
          <td>{{ date }}</td>
          <td>{{ formatNumber(entry.dailyCheckin ?? 0) }}</td>
          <td>{{ formatNumber(entry.writePost ?? 0) }}</td>
          <td>{{ formatNumber(entry.writeComment ?? 0) }}</td>
          <td>{{ formatNumber(entry.refJoin ?? 0) }}</td>
          <td>{{ formatNumber(entry.total ?? 0) }}</td>
        </tr>
      </tbody>
    </table>

    <h3 style="margin-top: 2rem">월별 소진 통계</h3>
    <table class="daily-table">
      <thead>
        <tr>
          <th>월</th>
          <th>divination</th>
          <th>fiveElementDaily</th>
          <th>hexagramToday</th>
          <th>합계</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(entry, month) in sortedMonthlyDeducts"
          :key="month"
          @click="selectMonth(month, 'deduct')"
        >
          <td>{{ month }}</td>
          <td>{{ formatNumber(entry.divination ?? 0) }}</td>
          <td>{{ formatNumber(entry.fiveElementDaily ?? 0) }}</td>
          <td>{{ formatNumber(entry.hexagramToday ?? 0) }}</td>
          <td>{{ formatNumber(entry.total ?? 0) }}</td>
        </tr>
      </tbody>
    </table>

    <table
      class="daily-table"
      v-if="selectedMonth && selectedMode === 'deduct'"
    >
      <thead>
        <tr>
          <th>일자</th>
          <th>divination</th>
          <th>fiveElementDaily</th>
          <th>hexagramToday</th>
          <th>합계</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(entry, date) in sortedDailyDeducts" :key="date">
          <td>{{ date }}</td>
          <td>{{ formatNumber(entry.divination ?? 0) }}</td>
          <td>{{ formatNumber(entry.fiveElementDaily ?? 0) }}</td>
          <td>{{ formatNumber(entry.hexagramToday ?? 0) }}</td>
          <td>{{ formatNumber(entry.total ?? 0) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
  import { ref, onMounted, computed } from 'vue'
  import { collection, collectionGroup, getDocs } from 'firebase/firestore'
  import { db } from '@/firebase'
  import { DateTime } from 'luxon'

  const totalCharged = ref(0)
  const totalRewarded = ref(0)
  const totalDeducted = ref(0)
  const totalPaid = ref(0)
  const monthlyCharges = ref({})
  const monthlyRewards = ref({})
  const monthlyDeducts = ref({})
  const dailyCharges = ref({})
  const dailyDeducts = ref({})
  const dailyRewards = ref({})
  const selectedMonth = ref(null)
  const selectedMode = ref(null)

  function formatNumber(n) {
    return (n ?? 0).toLocaleString()
  }

  onMounted(async () => {
    // charges: 총 결제 금액 (totalPrice)만 계산
    const chargesSnap = await getDocs(collection(db, 'charges'))
    chargesSnap.forEach((doc) => {
      const data = doc.data()
      const price = data.price ?? 0
      const purchasedAt = data.purchased_at || data.purchasedAt
      if (!purchasedAt) return

      const monthKey = purchasedAt.slice(0, 7)
      const dayKey = purchasedAt.slice(0, 10)

      totalPaid.value += price

      if (!monthlyCharges.value[monthKey])
        monthlyCharges.value[monthKey] = { totalPrice: 0 }
      monthlyCharges.value[monthKey].totalPrice += price

      if (!dailyCharges.value[monthKey]) dailyCharges.value[monthKey] = {}
      if (!dailyCharges.value[monthKey][dayKey])
        dailyCharges.value[monthKey][dayKey] = { totalPrice: 0 }
      dailyCharges.value[monthKey][dayKey].totalPrice += price
    })

    // lydia_history: 실제 지급된 LYD와 보상/차감 통계 계산
    const snapshot = await getDocs(collectionGroup(db, 'lydia_history'))
    snapshot.forEach((doc) => {
      const data = doc.data()
      const { type, amount = 0, menuUsed: menu, createdAt } = data

      if (!createdAt || !menu) return

      // Firestore 타임스탬프를 Luxon DateTime 객체로 변환하고 KST 시간대로 설정
      const createdAtKST = DateTime.fromISO(createdAt, { zone: 'Asia/Seoul' })

      // 유효한 날짜인지 확인
      if (!createdAtKST.isValid) {
        console.error('Invalid createdAt date:', createdAt)
        return
      }

      const monthKey = createdAtKST.toFormat('yyyy-MM') // 'YYYY-MM' 형식
      const dayKey = createdAtKST.toFormat('yyyy-MM-dd') // 'YYYY-MM-DD' 형식

      if (type === 'charge' && menu === 'payment') {
        totalCharged.value += amount

        if (!monthlyCharges.value[monthKey])
          monthlyCharges.value[monthKey] = { totalPrice: 0, totalLydia: 0 }
        if (!('totalLydia' in monthlyCharges.value[monthKey]))
          monthlyCharges.value[monthKey].totalLydia = 0
        monthlyCharges.value[monthKey].totalLydia += amount

        if (!dailyCharges.value[monthKey]) dailyCharges.value[monthKey] = {}
        if (!dailyCharges.value[monthKey][dayKey])
          dailyCharges.value[monthKey][dayKey] = {
            totalPrice: 0,
            totalLydia: 0,
          }
        if (!('totalLydia' in dailyCharges.value[monthKey][dayKey]))
          dailyCharges.value[monthKey][dayKey].totalLydia = 0
        dailyCharges.value[monthKey][dayKey].totalLydia += amount
      } else if (type === 'reward') {
        totalRewarded.value += amount

        if (!monthlyRewards.value[monthKey]) {
          monthlyRewards.value[monthKey] = {
            dailyCheckin: 0,
            writePost: 0,
            writeComment: 0,
            refJoin: 0,
            total: 0,
          }
        }
        if (!dailyRewards.value[monthKey]) dailyRewards.value[monthKey] = {}
        if (!dailyRewards.value[monthKey][dayKey]) {
          dailyRewards.value[monthKey][dayKey] = {
            dailyCheckin: 0,
            writePost: 0,
            writeComment: 0,
            refJoin: 0,
            total: 0,
          }
        }

        if (menu in monthlyRewards.value[monthKey]) {
          monthlyRewards.value[monthKey][menu] += amount
          dailyRewards.value[monthKey][dayKey][menu] += amount
        }

        monthlyRewards.value[monthKey].total += amount
        dailyRewards.value[monthKey][dayKey].total += amount
      } else if (type === 'deduct') {
        totalDeducted.value += amount

        if (!monthlyDeducts.value[monthKey]) {
          monthlyDeducts.value[monthKey] = {
            divination: 0,
            fiveElementDaily: 0,
            hexagramToday: 0,
            total: 0,
          }
        }
        if (!dailyDeducts.value[monthKey]) dailyDeducts.value[monthKey] = {}
        if (!dailyDeducts.value[monthKey][dayKey]) {
          dailyDeducts.value[monthKey][dayKey] = {
            divination: 0,
            fiveElementDaily: 0,
            hexagramToday: 0,
            total: 0,
          }
        }

        if (menu in monthlyDeducts.value[monthKey])
          monthlyDeducts.value[monthKey][menu] += amount
        if (menu in dailyDeducts.value[monthKey][dayKey])
          dailyDeducts.value[monthKey][dayKey][menu] += amount

        monthlyDeducts.value[monthKey].total += amount
        dailyDeducts.value[monthKey][dayKey].total += amount
      }
    })
  })

  const selectMonth = (month, mode) => {
    selectedMonth.value = month
    selectedMode.value = mode
  }

  const sortedMonthlyCharges = computed(() => {
    return Object.fromEntries(
      Object.entries(monthlyCharges.value).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })

  const sortedMonthlyRewards = computed(() => {
    return Object.fromEntries(
      Object.entries(monthlyRewards.value).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })

  const sortedMonthlyDeducts = computed(() => {
    return Object.fromEntries(
      Object.entries(monthlyDeducts.value).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })

  const sortedDailyCharges = computed(() => {
    if (!selectedMonth.value || !dailyCharges.value[selectedMonth.value])
      return {}
    return Object.fromEntries(
      Object.entries(dailyCharges.value[selectedMonth.value]).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })

  const sortedDailyRewards = computed(() => {
    if (!selectedMonth.value || !dailyRewards.value[selectedMonth.value])
      return {}
    return Object.fromEntries(
      Object.entries(dailyRewards.value[selectedMonth.value]).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })

  const sortedDailyDeducts = computed(() => {
    if (!selectedMonth.value || !dailyDeducts.value[selectedMonth.value])
      return {}
    return Object.fromEntries(
      Object.entries(dailyDeducts.value[selectedMonth.value]).sort((a, b) =>
        b[0].localeCompare(a[0]),
      ),
    )
  })
</script>

<style scoped>
  .summary-container {
    padding: 1rem;
    width: var(--device-width);
    max-width: 480px;
    margin: 0 auto;
  }
  .summary-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .card {
    flex: 1 1 48%;
    font-size: 0.9rem;
    background: #f9f9f9;
    border: 1px solid #ccc;
    border-radius: 6px;
    padding: 0.5rem;
    text-align: center;
  }
  .daily-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    margin-top: 1rem;
  }
  .daily-table th,
  .daily-table td {
    border: 1px solid #ccc;
    padding: 0.3rem;
    text-align: center;
  }
</style>
